/**
 * Activate account from email link (Djoser uid + token).
 */

import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import Seo from '../components/Seo';
import './Login.css';

const ActivateAccount = () => {
  const { uid, token } = useParams();
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading | ok | error
  const [error, setError] = useState('');
  const startedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    const activate = async () => {
      if (!uid || !token) {
        setStatus('error');
        setError(t('login.activateInvalid'));
        return;
      }
      // StrictMode (dev) remounts once; avoid a second POST that looks like “expired”.
      if (startedRef.current) {
        return;
      }
      startedRef.current = true;
      try {
        await axios.post(
          '/api/auth/users/activation/',
          { uid, token },
          {
            headers: {
              'Accept-Language': lang === 'en' ? 'en' : 'es',
              'X-ClarCV-Lang': lang === 'en' ? 'en' : 'es',
            },
          }
        );
        if (!cancelled) {
          setStatus('ok');
          setTimeout(() => navigate(`/login?next=${encodeURIComponent('/dashboard/upload')}`), 2500);
        }
      } catch (err) {
        if (cancelled) return;
        const data = err.response?.data;
        const detail = (
          data?.detail
          || data?.token?.[0]
          || data?.uid?.[0]
          || data?.non_field_errors?.[0]
          || ''
        ).toString().toLowerCase();
        // Djoser / scanners: link already used or account already active.
        if (
          detail.includes('stale')
          || detail.includes('invalid')
          || detail.includes('activ')
          || err.response?.status === 403
          || err.response?.status === 400
        ) {
          setError(t('login.activateInvalid'));
        } else {
          setError(typeof data?.detail === 'string' ? data.detail : t('login.activateInvalid'));
        }
        setStatus('error');
      }
    };

    activate();
    return () => {
      cancelled = true;
    };
  }, [uid, token, lang, navigate, t]);

  return (
    <div className="auth-page">
      <Seo
        title={t('login.activateTitle')}
        description={t('login.activateLead')}
        path="/activate"
        noindex
      />
      <div className="auth-card">
        <h1 className="auth-card__title">{t('login.activateTitle')}</h1>
        <p className="auth-card__lead">{t('login.activateLead')}</p>

        {status === 'loading' && (
          <div className="auth-alert auth-alert--success" role="status">
            <span>{t('login.activateLoading')}</span>
          </div>
        )}
        {status === 'ok' && (
          <>
            <div className="auth-alert auth-alert--success" role="status">
              <span>{t('login.activateSuccess')}</span>
            </div>
            <Link to="/login?next=/dashboard/upload" className="btn btn-primary auth-card__submit">
              {t('login.titleLogin')}
            </Link>
          </>
        )}
        {status === 'error' && (
          <>
            <div className="auth-alert auth-alert--error" role="alert">
              <span>{error}</span>
            </div>
            <Link to="/login" className="btn btn-primary auth-card__submit">
              {t('login.titleLogin')}
            </Link>
          </>
        )}

        {status === 'loading' && (
          <Link to="/login" className="auth-card__back">
            ← {t('login.titleLogin')}
          </Link>
        )}
      </div>
    </div>
  );
};

export default ActivateAccount;
